// 统一的数据类型定义 - 与后端保持一致

// ==================== AI批改结果相关类型 ====================
// 完全匹配后端 ai/types.go 的数据结构

// 分数摘要
export interface ScoreSummary {
  total_score: number;    // 总分（1-25分）
  grade_level: string;    // 档次等级
  word_count: number;     // 实际词数
  word_penalty: number;   // 词数扣分
}

// 单项维度得分
export interface DimensionScore {
  score: number;          // 得分
  max_score: number;      // 满分值
  comment: string;        // 评价说明
}

// 各维度得分
export interface DimensionScores {
  language: DimensionScore;   // 语言维度
  content: DimensionScore;    // 内容维度
  coherence: DimensionScore;  // 连贯性维度
  creativity: DimensionScore; // 创意维度
}

// 语法错误
export interface GrammarError {
  type: string;         // 错误类型
  sentence: string;     // 原句
  error_part: string;   // 错误部分
  correction: string;   // 修正建议
  explanation: string;  // 错误解释
}

// 词汇错误
export interface VocabularyError {
  type: string;         // 错误类型
  original: string;     // 原词
  suggested: string;    // 建议用词
  context: string;      // 上下文
  reason: string;       // 修改原因
}

// 结构问题
export interface StructureIssue {
  issue: string;        // 问题描述
  location: string;     // 位置
  suggestion: string;   // 改进建议
}

// 错误分析
export interface ErrorAnalysis {
  grammar_errors: GrammarError[];      // 语法错误列表
  vocabulary_errors: VocabularyError[]; // 词汇错误列表
  structure_issues: StructureIssue[];   // 结构问题列表
}

// 亮点部分
export interface HighlightSection {
  excellent_sentences: string[];  // 优秀句子列表
  advanced_vocabulary: string[];  // 高级词汇列表
  good_expressions: string[];     // 好的表达列表
}

// 改进建议
export interface ImprovementSuggestion {
  aspect: string;        // 改进方面
  current_level: string; // 当前水平
  suggestion: string;    // 具体建议
  example: string;       // 示例说明
}

// 反馈部分
export interface FeedbackSection {
  overall_comment: string;                           // 总体评价
  strengths: string[];                               // 优点列表
  weaknesses: string[];                              // 不足列表
  improvement_suggestions: ImprovementSuggestion[];  // 改进建议列表
}

// 主要修改
export interface MajorChange {
  original: string;  // 原文片段
  revised: string;   // 修改后片段
  reason: string;    // 修改理由
}

// 修改后版本
export interface CorrectedVersionSection {
  full_text: string;               // 修改后的完整作文
  major_changes: MajorChange[];    // 主要修改记录
}

// 增强版批改结果 - 后端 CorrectEssayHandler 返回的完整结构
export interface EnhancedCorrectionResult {
  score_summary: ScoreSummary;            // 分数摘要
  dimension_scores: DimensionScores;      // 各维度得分
  error_analysis: ErrorAnalysis;          // 错误分析
  highlights: HighlightSection;           // 亮点识别
  feedback: FeedbackSection;              // 反馈建议
  corrected_version: CorrectedVersionSection; // 修改后版本
}

// ==================== 数据库模型相关类型 ====================

// 学生统计信息
export interface StudentStats {
  essay_count: number;
  average_score: number;
}

// 学生信息（与后端StudentResponse对应）
export interface Student {
  id: number;
  name: string;
  class_id: number;
  email?: string;
  phone?: string;
  class?: Class;  // 嵌套的班级对象
  stats: StudentStats;  // 统计信息
  created_at: string;
  updated_at: string;
  // 兼容旧的字段（用于其他页面）
  teacher_id?: number;
  student_number?: string;
}

// 班级信息
export interface Class {
  id: number;
  teacher_id: number;
  name: string;
  grade: number;
  description?: string;
  student_count?: number;
  created_at: string;
  updated_at: string;
}

// 批改结果 - 完全匹配后端新的Correction模型
export interface Correction {
  id: number;
  essay_id: number;
  
  // score_summary
  total_score: number;
  grade_level: string;
  word_count: number;
  word_penalty: number;
  
  // dimension_scores - 各维度分数和评价
  language_score: number;
  language_comment: string;
  content_score: number;
  content_comment: string;
  coherence_score: number;
  coherence_comment: string;
  creativity_score: number;
  creativity_comment: string;
  
  // JSON数据字段
  error_analysis: string;
  highlights: string;
  feedback: string;
  major_changes: string;
  ai_response: string;
  
  // 文本字段
  corrected_text: string;
  
  created_at: string;
}

// 错误记录 - 与后端 ErrorRecord 模型完全一致
export interface ErrorRecord {
  id: number;
  essay_id: number;
  error_type: string;  // grammar, vocabulary, spelling, structure
  error_text: string;  // 错误的文本
  suggestion: string;  // 建议修改
  line_number: number;
  position: number;
  severity: string;    // low, medium, high
  created_at: string;
}

// 作文类型常量
export const EssayTypes = {
  APPLICATION: 'application',   // 应用文写作
  CONTINUATION: 'continuation'  // 读后续写
} as const;

export type EssayType = typeof EssayTypes[keyof typeof EssayTypes];

// 作文题目信息
export interface EssayTopic {
  id: number;
  teacher_id: number;
  title: string;
  topic?: string;
  requirements?: string;
  essay_type: EssayType;
  word_limit: number;
  is_active: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

// 作文信息 - 完全匹配后端的 Essay 模型
export interface Essay {
  id: number;
  topic_id: number;        // 题目ID
  teacher_id: number;
  student_id: number;
  class_id: number;
  batch_id?: string;      // 批次ID
  original_file?: string;
  original_text?: string;
  corrected_text?: string;
  word_count: number;
  status: 'pending' | 'processing' | 'correcting' | 'completed' | 'failed';
  submitted_at: string;
  corrected_at?: string;
  // 关联对象 - 使用小写，与后端 JSON 标签一致
  topic?: EssayTopic;      // 题目对象
  teacher?: any;
  student?: Student;
  class?: Class;
  correction?: Correction;
  errors?: ErrorRecord[];
  created_at?: string;
  updated_at?: string;
}

// API响应格式
export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

// 分页响应格式
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
}

// 批改结果详情（包含所有相关信息）
export interface CorrectionDetail {
  correction: Correction;
  errors: ErrorRecord[];
  essay: Essay;
  dimension_scores?: DimensionScores;  // 从后端返回的完整维度分数信息
}